import { NextPage } from 'next';
import dynamic from 'next/dynamic';
import { PageViewTracker, template } from '@belong/common';
import { GoogleStars, Link, LinkBase, Text } from '@belong/ui';
import type { PrismicDocument } from '@prismicio/types';
import clsx from 'clsx';
import { kebabCase } from 'es-toolkit/compat';
import type { UnitListingModel, ReviewModel } from 'src/api/models';
import { type IntercomWidgetProps } from 'src/components/intercom-widget/intercom-widget';
import { MetaSEO } from 'src/components/meta-seo/meta-seo';
import { TestimonialReviews } from 'src/components/testimonial-reviews/testimonial-reviews';
import { TestimonialVideos } from 'src/components/testimonial-videos/testimonial-videos';
import { useRegions } from 'src/features/app';
import { FaqSection } from 'src/features/home-page/components/FaqSection/FaqSection';
import { getGoogleMyBusinessProfile } from 'src/features/homeowners-city/utils';
import { NeighborhoodList } from 'src/features/homeowners-neighborhood/components/neighborhood-list/neighborhood-list';
import { BlogCarousel } from 'src/features/homeowners/components/blog-carousel/blog-carousel';
import { CalculatorSectionVariant } from 'src/features/homeowners/components/calculator-section/calculator-section-variant';
import { GttlBelovedHome } from 'src/features/homeowners/components/gttl-landing-page/components/gttl-beloved-home/gttl-beloved-home';
import { GttlGuaranteedRent } from 'src/features/homeowners/components/gttl-landing-page/components/gttl-guaranteed-rent/gttl-guaranteed-rent';
import { GttlResidentsLove } from 'src/features/homeowners/components/gttl-landing-page/components/gttl-residents-love/gttl-residents-love';
import { PriceTablePreview } from 'src/features/homeowners/components/price-table-preview/price-table-preview';
import { StaticLandingPageLink } from 'src/features/homeowners/homeowners.types';
import { type School } from 'src/features/homeowners-neighborhood/types';
import { ListingsCarousel } from 'src/features/listing-page/components/listings-carousel/listings-carousel';
import { useStore } from 'src/store';
import { regionSelector } from 'src/store/region';
import { REGION_ARTICLES_MAP, SLUG_TO_CANONICAL_MAP } from '../../constants';
import { CityCover } from '../city-cover/city-cover';
import { DemographicsSection } from '../demographics-section/demographics-section';
import { GmbProfile } from '../gmb-profile/gmb-profile';
import { NearbySchoolsSection } from '../nearby-schools/nearby-schools';
import { RentalStatsSection } from '../rental-stats-section/rental-stats-section';
import { SocialProof } from '../social-proof/social-proof';
import { CITY_LANDING_PAGE_STRINGS } from './cities-landing-page-strings';
import css from './cities-landing-page.module.css';

const IntercomWidget = dynamic<IntercomWidgetProps>(
  () =>
    import('src/components/intercom-widget/intercom-widget').then(
      (mod) => mod.IntercomWidget,
    ),
  { ssr: false },
);

export const CitiesLandingPage: NextPage<CitiesLandingPageProps> = ({
  cityData,
  listings,
  blogPosts,
  coverSrc,
  reviews,
}) => {
  const {
    city: cityNameRaw,
    cityAdditional,
    neighborhoods,
    populationProper,
    incomeHouseholdMedian,
    cityLocations,
    married,
    homeOwnership,
    homeValue,
    commuteTime,
    schools,
    stateId,
  } = cityData;

  const { description = '' } = cityAdditional || {};
  const region = useStore(regionSelector);

  const { regions } = useRegions();

  const cityName = cityNameRaw.replace(new RegExp(' ', 'g'), '\u00a0');

  const metaTitle = template(CITY_LANDING_PAGE_STRINGS['meta.title'], {
    cityName: cityNameRaw,
    stateId,
  }) as string;

  const metaDescription = template(
    CITY_LANDING_PAGE_STRINGS['meta.description'],
    {
      cityName: cityNameRaw,
      stateId,
    },
  ) as string;

  const cityRegion = regions.find(
    (region) => region.name === cityData.region.name,
  );
  const citySlug = `${kebabCase(cityNameRaw)}-${stateId.toLowerCase()}`;

  const gmbLink = getGoogleMyBusinessProfile({
    city: citySlug,
    regionId: region.id,
  });

  // `?? ''` rather than indexing with `undefined`: an unknown region name and
  // a missing one both mean "no banner".
  const RegionBanner = REGION_ARTICLES_MAP[cityRegion?.name ?? ''];

  return (
    <PageViewTracker name="homeowners_city_landing_page">
      <IntercomWidget isEnabled />
      <MetaSEO
        title={metaTitle}
        description={metaDescription}
        canonical={SLUG_TO_CANONICAL_MAP[citySlug]}
        openGraph={{
          title: metaTitle,
          description: metaDescription,
        }}
      />
      <CityCover
        cityName={cityName}
        phoneNumber={region?.information?.contactInformation?.phone}
        src={coverSrc}
      />
      <GttlGuaranteedRent />
      <GttlResidentsLove />
      <GttlBelovedHome />
      <div>
        <div className="mt-2xl container flex flex-col items-center">
          {RegionBanner && (
            <article className="mb-5xl w-full">
              <RegionBanner />
            </article>
          )}
          <div className="mb-5xl xl:mb-6xl relative w-full">
            <SocialProof regionName={region?.name} />
          </div>
          <div className="mb-3xl container">
            <Text
              variant="h2"
              fontWeight="semibold"
              className={clsx(
                'mb-xl lg:mb-sm mx-auto text-center',
                css.descriptionTitle,
              )}
            >
              {template(CITY_LANDING_PAGE_STRINGS['descriptionSection.title'], {
                cityName,
              })}
            </Text>
            {description && <Text className="text-center">{description}</Text>}
          </div>
          <div className="mb-3xl container w-full">
            <Text variant="h3" fontWeight="semibold" className="mb-sm lg:mb-xl">
              Demographics
            </Text>
            <DemographicsSection
              population={populationProper}
              medianHouseholdIncome={incomeHouseholdMedian}
              percentMarried={married}
            />
          </div>
          <div
            className={clsx(
              'mb-3xl container w-full',
              !schools?.length && 'mb-5xl xl:mb-6xl',
            )}
          >
            <Text variant="h3" fontWeight="semibold" className="mb-sm lg:mb-xl">
              Rental Stats
            </Text>
            <RentalStatsSection
              percentRent={100 - homeOwnership}
              homeValue={homeValue}
              commuteTime={commuteTime}
            />
          </div>
          {!!cityLocations?.length && (
            <div className="mb-3xl container w-full">
              <Text variant="h3" fontWeight="semibold" className="mb-sm">
                {cityName} Local Destinations
              </Text>
              <div className="gap-y-sm border-gray p-sm grid grid-cols-1 rounded border border-solid lg:grid-cols-2">
                {cityLocations.map((location, index) => {
                  return (
                    <LinkBase
                      href={location.website}
                      target="_blank"
                      key={index}
                    >
                      <Text className="duration-hover hover:text-dark-navy transition-all">
                        • {location.name}
                      </Text>
                    </LinkBase>
                  );
                })}
              </div>
            </div>
          )}
          {!!schools?.length && (
            <div className="mb-5xl xl:mb-6xl container w-full">
              <Text
                variant="h3"
                fontWeight="semibold"
                className="mb-sm lg:mb-xl"
              >
                Nearby Schools
              </Text>
              <NearbySchoolsSection schools={schools} />
            </div>
          )}
          <div className="mb-5xl md:mb-6xl container">
            <CalculatorSectionVariant />
          </div>
        </div>

        <div className="mb-5xl xl:mb-6xl flex flex-col">
          <div className="container">
            <div className="mb-sm">
              <Text
                variant="h2"
                fontWeight="semibold"
                className={clsx(
                  'mx-auto text-center md:max-w-none',
                  css.reviewsTitle,
                )}
              >
                {CITY_LANDING_PAGE_STRINGS['testimonials.title']}
              </Text>
            </div>
            <div className="mb-xl">
              <Text
                variant="h3"
                className={clsx(
                  'mx-auto text-center md:max-w-none',
                  css.reviewsSubtitle,
                )}
              >
                {template(CITY_LANDING_PAGE_STRINGS['testimonials.text'], {
                  cityName,
                })}
              </Text>
            </div>
            <div className="mb-2xl flex justify-center">
              <GoogleStars />
            </div>
          </div>
          <div className="carousel-container mb-5xl xl:mb-6xl">
            <TestimonialReviews reviews={reviews} />
          </div>
          <div className="container">
            <Text
              variant="h2"
              fontWeight="semibold"
              className={clsx(
                'mx-auto text-center lg:max-w-none',
                css.videoTestimonialsTitle,
              )}
            >
              {CITY_LANDING_PAGE_STRINGS['video.testimonials.title']}
            </Text>
          </div>
          <div className="carousel-container mt-xl md:mt-2xl mx-auto flex justify-center">
            <TestimonialVideos />
          </div>
        </div>

        <div className="mb-3xl container flex flex-col items-center">
          <PriceTablePreview
            title="Pricing aligned with you."
            subtitle={'No hidden fees. Just\u00a0great\u00a0service.'}
            regionId={region.id}
            showMore="See More"
            showLess="See Less"
            enableToggleExpansion={false}
          />
        </div>
        {listings.length > 0 && (
          <div className="mb-5xl xl:mb-6xl w-full">
            <div className="container md:contents">
              <Text variant="h2" className="text-center" fontWeight="semibold">
                {CITY_LANDING_PAGE_STRINGS['listings.title'](cityName)}
              </Text>
              <div className="mt-2xl">
                <ListingsCarousel units={listings} />
              </div>
            </div>
          </div>
        )}

        {blogPosts.length > 0 && (
          <div className="mb-5xl bg-darker-navy py-5xl md:mb-6xl flex w-full flex-col items-center">
            <Text
              variant="h2"
              fontWeight="semibold"
              className="mb-lg md:mb-xl text-white"
            >
              {CITY_LANDING_PAGE_STRINGS['blog.title']}
            </Text>
            <div className={clsx('carousel-container mb-lg md:mb-xl')}>
              <BlogCarousel
                blogPosts={blogPosts}
                theme="dark"
                blurredEnds={false}
              />
            </div>
            <Link
              href="/blog"
              variant="solid"
              buttonType="primary"
              size="large"
            >
              {CITY_LANDING_PAGE_STRINGS['blog.link']}
            </Link>
          </div>
        )}
        <div className="container flex flex-col items-center">
          {gmbLink && (
            <div className="mb-5xl xl:mb-6xl container w-full">
              <GmbProfile gmbLink={gmbLink} />
            </div>
          )}
          <div className="mb-5xl xl:mb-6xl container w-full">
            <FaqSection />
          </div>
          {neighborhoods.length > 0 && (
            <div className={clsx('mb-5xl xl:mb-6xl w-full', css.neighborhoods)}>
              <Text variant="h2" className="text-center" fontWeight="semibold">
                <span className="hidden sm:block">
                  {template(CITY_LANDING_PAGE_STRINGS['neighborhoods.title'], {
                    cityName,
                  })}
                </span>
                <span className="block sm:hidden">
                  {template(CITY_LANDING_PAGE_STRINGS['neighborhoods.title'], {
                    cityName,
                  })}
                </span>
              </Text>
              <Text variant="h3" className="mb-xl mt-xs text-center">
                {CITY_LANDING_PAGE_STRINGS['neighborhoods.text']}
              </Text>
              <NeighborhoodList neighborhoods={neighborhoods} />
            </div>
          )}
        </div>
      </div>
    </PageViewTracker>
  );
};

export type CitiesLandingPageProps = {
  cityData: {
    city: string;
    cityLocations?: {
      name: string;
      website: string;
    }[];
    cityAdditional: {
      description: string;
      slug: string;
    };
    neighborhoods: StaticLandingPageLink[];
    // `region` — the display name, e.g. "San Francisco Bay Area" — is what
    // the page layout puts in the `<h1>`; it was missing from this shape.
    region: {
      id: number;
      name: string;
      region?: string;
      state: string;
      status: string;
    };
    stateId: string;
    populationProper: number;
    incomeHouseholdMedian: number;
    married: number;
    homeOwnership: number;
    homeValue: number;
    commuteTime: number;
    schools?: School[];
  };
  listings: UnitListingModel[];
  blogPosts: PrismicDocument[];
  coverSrc: string;
  reviews: ReviewModel[];
};
